import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import { updateReportStatus, deleteReport } from '../../services/reportService';
import { BRAND } from '../../theme/brandTheme';
import BrandReportsList from './BrandReportsList';
import ReportFilterBar from './ReportFilterBar';
import TestPipeline from './TestPipeline';
import DashboardEmptyState from './DashboardEmptyState';
import showNotification from '../common/Notification';
import {
  applyReportFilters,
  filtersToSearchParams,
  parseFiltersFromSearchParams,
} from '../../utils/reportFilterUtils';
import { canWriteReports } from '../../utils/roleUtils';

/** Shared report list / pipeline section for dashboard and brand pages */
const ReportsSection = ({
  reports,
  setReports,
  user,
  title = 'Raporlar',
  hideBrandHeader = false,
  expandAllTests = false,
  accentColor,
}) => {
  const accent = accentColor || BRAND.purple;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reportFilters, setReportFilters] = useState(() =>
    parseFiltersFromSearchParams(searchParams),
  );
  const dashboardView = searchParams.get('view') === 'pipeline' ? 'pipeline' : 'list';
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [apiLoading, setApiLoading] = useState({});
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    reportId: null,
    reportName: '',
    brandName: '',
  });

  const { brands: displayReports, totalCount, visibleCount } = useMemo(
    () => applyReportFilters(reports, reportFilters),
    [reports, reportFilters],
  );

  const handleFiltersChange = useCallback(
    (next) => {
      setReportFilters(next);
      const params = filtersToSearchParams(next);
      if (dashboardView === 'pipeline') params.set('view', 'pipeline');
      setSearchParams(params, { replace: true });
    },
    [setSearchParams, dashboardView],
  );

  const handleViewChange = useCallback(
    (_, nextView) => {
      if (!nextView) return;
      const params = filtersToSearchParams(reportFilters);
      if (nextView === 'pipeline') params.set('view', 'pipeline');
      else params.delete('view');
      setSearchParams(params, { replace: true });
    },
    [reportFilters, setSearchParams],
  );

  const canEditPipeline = canWriteReports(user) && (user?.role === 'admin' || user?.role === 'editor');

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest('.status-dropdown')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeDropdown]);

  const handleStatusChange = async (reportId, newStatus) => {
    try {
      setApiLoading((prev) => ({ ...prev, [reportId]: true }));
      setActiveDropdown(`loading-${reportId}`);
      await updateReportStatus(reportId, newStatus);

      setReports((currentReports) =>
        currentReports.map((brand) => {
          const brandTests = [...brand.tests];
          let testIndex = -1;
          let oldStatus = '';

          for (let i = 0; i < brandTests.length; i++) {
            if (brandTests[i].id === reportId) {
              testIndex = i;
              oldStatus = brandTests[i].status;
              brandTests[i] = { ...brandTests[i], status: newStatus };
              break;
            }
          }

          if (testIndex === -1) return brand;

          const updatedStats = { ...brand.stats };
          if (oldStatus === 'Taslak') updatedStats.taslak -= 1;
          else if (oldStatus === 'Canlı') updatedStats.canli -= 1;
          else if (oldStatus === 'Durduruldu') updatedStats.durduruldu -= 1;

          if (newStatus === 'Taslak') updatedStats.taslak += 1;
          else if (newStatus === 'Canlı') updatedStats.canli += 1;
          else if (newStatus === 'Durduruldu') updatedStats.durduruldu += 1;

          return { ...brand, tests: brandTests, stats: updatedStats };
        }),
      );

      showNotification('Durum başarıyla güncellendi', 'success');
    } catch (err) {
      console.error('Status update error:', err);
      const msg = err.response?.data?.error || 'Durum güncellenirken bir hata oluştu';
      showNotification(msg, 'error');
    } finally {
      setApiLoading((prev) => ({ ...prev, [reportId]: false }));
      setActiveDropdown(null);
    }
  };

  const toggleDropdown = (id, e) => {
    if (user?.role !== 'admin' && user?.role !== 'editor') return;
    e.stopPropagation();
    if (activeDropdown?.startsWith('loading-')) return;
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  const handleReportDetail = useCallback(
    (report) => {
      if (report?.id) navigate(`/dashboard/reports/${report.id}`);
    },
    [navigate],
  );

  const openDeleteDialog = (reportId, reportName, brandName) => {
    setDeleteDialog({ open: true, reportId, reportName, brandName });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, reportId: null, reportName: '', brandName: '' });
  };

  const confirmDeleteReport = async () => {
    try {
      const { reportId, reportName } = deleteDialog;
      await deleteReport(reportId);

      setReports((currentReports) =>
        currentReports.map((brand) => {
          const updatedTests = brand.tests.filter((test) => test.id !== reportId);
          if (updatedTests.length >= brand.tests.length) return brand;

          const deletedTest = brand.tests.find((test) => test.id === reportId);
          const updatedStats = { ...brand.stats };
          if (deletedTest) {
            updatedStats.toplam -= 1;
            if (deletedTest.status === 'Taslak') updatedStats.taslak -= 1;
            else if (deletedTest.status === 'Canlı') updatedStats.canli -= 1;
            else if (deletedTest.status === 'Durduruldu') updatedStats.durduruldu -= 1;
          }

          return { ...brand, tests: updatedTests, stats: updatedStats };
        }),
      );

      showNotification(`${reportName} raporu başarıyla silindi`, 'success');
    } catch (err) {
      console.error('Delete error:', err);
      showNotification(`Rapor silinirken hata: ${err.message}`, 'error');
    } finally {
      closeDeleteDialog();
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 2,
          mb: 2.5,
          borderRadius: 2.5,
          border: `1px solid ${BRAND.borderLight}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          bgcolor: '#fff',
        }}
      >
        <Box>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: accent }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: BRAND.textMuted, mt: 0.25 }}>
            {visibleCount} / {totalCount} rapor görüntüleniyor
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={dashboardView}
          exclusive
          onChange={handleViewChange}
          size="small"
          aria-label="Görünüm"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 600,
              px: 1.5,
              borderColor: BRAND.borderLight,
              '&.Mui-selected': {
                bgcolor: `${accent}12`,
                color: accent,
                borderColor: `${accent}44`,
              },
            },
          }}
        >
          <ToggleButton value="list" aria-label="Liste görünümü">
            <ViewListIcon sx={{ mr: 0.5, fontSize: 18 }} />
            Liste
          </ToggleButton>
          <ToggleButton value="pipeline" aria-label="Pipeline görünümü">
            <ViewKanbanIcon sx={{ mr: 0.5, fontSize: 18 }} />
            Pipeline
          </ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      <ReportFilterBar
        filters={reportFilters}
        onChange={handleFiltersChange}
        totalCount={totalCount}
        visibleCount={visibleCount}
      />

      {totalCount === 0 ? (
        <DashboardEmptyState />
      ) : visibleCount === 0 ? (
        <DashboardEmptyState filteredOut />
      ) : dashboardView === 'pipeline' ? (
        <TestPipeline
          reports={displayReports}
          canDrag={canEditPipeline}
          onStatusChange={handleStatusChange}
          onReportDetail={handleReportDetail}
          apiLoading={apiLoading}
        />
      ) : (
        <BrandReportsList
          reports={displayReports}
          activeDropdown={activeDropdown}
          apiLoading={apiLoading}
          user={user}
          onToggleDropdown={toggleDropdown}
          onStatusChange={handleStatusChange}
          onReportDetail={handleReportDetail}
          onDeleteReport={openDeleteDialog}
          hideBrandHeader={hideBrandHeader}
          expandAllTests={expandAllTests}
        />
      )}

      <Dialog open={deleteDialog.open} onClose={closeDeleteDialog}>
        <DialogTitle>Raporu Sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{deleteDialog.reportName}</strong> raporunu silmek istediğinize emin misiniz? Bu
            işlem geri alınamaz.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            İptal
          </Button>
          <Button onClick={confirmDeleteReport} color="error" autoFocus>
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportsSection;
